import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Linking, Platform } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { TokenType } from '@vatom/sdk/core'
import { useAccount, useConfig, useThrottle, useUser } from '@vatom/sdk/react'
import { translate } from '@vatom/utils'
import { VatomModelType } from '@vatom/vatomNew/plugin'
import { useVatomWalletSdkStore } from '@vatom/wallet-sdk'
import { HeaderBackOptions, Status, theme, Toast } from '@vatom/wombo'
import { observer } from 'mobx-react-lite'
import { Box, Image, Text } from 'native-base'

import CloseIcon from '../../../assets/close.min.svg'
import Alert from '../../../components/Alert'
import { useAnalytics } from '../../../hooks/useAnalytics'
import { AppRoutes } from '../../../navigators'
import { VatomWallet } from '../../../utils/WalletSdk'
import {
  ActionSheetDelete,
  ActionSheetDrop,
  ActionSheetFoundation,
  ActionSheetGeneralInfo,
  ActionSheetMint,
  ActionSheetSell,
  ActionSheetSend,
  ActionSheetShare,
  ActionSheetUnSell
} from '../../ActionSheets'
import { ThemeInterface } from '../../Business/WebBusinessProvider/types'
import EllipsisIcon from '../assets/ellipsis.svg'

import Share from './Share'

export interface INftDetailHeader {
  onClose: () => void
  theme?: ThemeInterface
  token: TokenType | VatomModelType
  contractAddress?: string
  owner?: string
}

export const NftDetailHeader = observer(
  forwardRef(
    ({ token, onClose, theme: businessTheme, contractAddress, owner }: INftDetailHeader, ref) => {
      const navigation = useNavigation()
      const [isSendOpen, setIsSendOpen] = useState(false)
      const [isShare, setIsShare] = useState(false)
      const [isSellOpen, setIsSellOpen] = useState(false)
      const [isShareOpen, setIsShareOpen] = useState(false)
      const [isFoundationOpen, setIsFoundationOpen] = useState(false)
      const [isMintOpen, setIsMintOpen] = useState(false)
      const [isUnSellOpen, setIsUnsellOpen] = useState(false)
      const [isGeneralInfoOpen, setIsGeneralInfoOpen] = useState(false)
      const [isDropOpen, setIsDropOpen] = useState(false)
      const [isDeleteOpen, setIsDeleteOpen] = useState(false)
      const { account } = useAccount()
      const config = useConfig()
      const user = useUser()
      const { analytics } = useAnalytics()
      const { isEmbedded, config: sdkConfig } = useVatomWalletSdkStore()
      const NftDetailHeaderSdk = sdkConfig.hideTokenActions
      const shareLink = token.shareInfo ?? ''
      const tokenName = token.metadata.name ?? ''
      const hideTokenActions = NftDetailHeaderSdk !== true
      const unlockKey = token.type === 'vatom' && token.unlockKey('engaged')

      useImperativeHandle(ref, () => ({
        openSheet: (name: string) => {
          switch (name) {
            case 'send':
              setIsSendOpen(true)
              break
            case 'share':
              setIsShare(true)
              setIsSendOpen(true)
              break
          }
        }
      }))

      const pickupAction = async () => {
        analytics.event(
          'initiateAction',
          {
            actionUri: 'Pickup',
            source: 'ObjectMenu'
          },
          token
        )
        navigation.navigate(AppRoutes.home)
        await token.performAction('Pickup')
        Toast({
          title: translate('mapScreen.pickupSuccess', {
            name: tokenName
          }),
          status: Status.success,
          placement: 'top'
        })
      }

      const shareAction = () => {
        analytics.event(
          'initiateAction',
          {
            actionUri: 'Clone',
            source: 'ObjectMenu'
          },
          token
        )

        setIsShare(true)
        setIsSendOpen(true)
        setIsFoundationOpen(false)
      }

      const shareLinkAction = async () => {
        const isShared = await Share.open({
          message: shareLink
        })

        if (!isShared) {
          analytics.event(
            'initiateAction',
            {
              actionUri: 'Share',
              source: 'ObjectMenu'
            },
            token
          )
          setIsShareOpen(true)
          setIsFoundationOpen(false)
        }
      }

      const sendAction = () => {
        analytics.event(
          'initiateAction',
          {
            actionUri: 'Send',
            source: 'ObjectMenu'
          },
          token
        )
        setIsSendOpen(true)
        setIsFoundationOpen(false)
      }

      const onSellAction = useThrottle(() => {
        const isValidSeller = account?.sellerAccount.status === 'enabled'
        const link = `${config.studioServer}/${user?.default_business_id}/settings/payouts`

        if (!isValidSeller) {
          Alert.fire({
            showCancelButton: true,
            title: 'Business Profile not Configured',
            text: 'You must first complete your business profile to receive payments.',
            confirmButtonText: 'Configure'
          }).then((alert: any) => {
            if (Platform.OS === 'web') {
              if (alert && alert.isConfirmed) window.open(link, '_blank')

              return
            }

            if (alert) Linking.openURL(link)
          })

          return null
        }

        setIsSellOpen(true)
        setIsFoundationOpen(false)
      }, 1000)

      const sellAction = () => {
        analytics.event(
          'initiateAction',
          {
            actionUri: 'Sell',
            source: 'ObjectMenu'
          },
          token
        )
        onSellAction()
      }

      const mintAction = () => {
        analytics.event(
          'initiateAction',
          {
            actionUri: 'Mint',
            source: 'ObjectMenu'
          },
          token
        )

        setIsMintOpen(true)
        setIsFoundationOpen(false)
      }

      const dropAction = () => {
        analytics.event(
          'initiateAction',
          {
            actionUri: 'Drop',
            source: 'ObjectMenu'
          },
          token
        )

        const spaces_domains = ['spaces.vatom.com', 'spaces.vatominc.cool', 'spaces.vatominc.cc']
        if (
          isEmbedded &&
          spaces_domains.includes(sessionStorage?.getItem('POPUP_HOSTNAME') ?? '')
        ) {
          // TODO: TEST THIS
          // await VatomWallet._shared
          //   .sendMsg('vatomwallet:listActionHandlers', { vatom: token.id })
          //   .then(e => {
          //     // Check if the host app wants to catch Drop messages
          //     isEmbedded = !!e?.overrideDropAction
          //   })

          // Check if host app wants to override this action
          VatomWallet._shared.sendMsgWithoutResponse('vatomwallet:handleAction', {
            action: 'Drop',
            vatomID: token.id
          })
          navigation.goBack()
        } else {
          setIsDropOpen(true)
          setIsFoundationOpen(false)
        }
      }

      const deleteAction = () => {
        analytics.event(
          'initiateAction',
          {
            actionUri: 'Delete',
            source: 'ObjectMenu'
          },
          token
        )
        setIsDeleteOpen(true)
        setIsFoundationOpen(false)
      }

      const actionOptionIcon = () => {
        try {
          if (config.features.customActionBtn) {
            const style = config.features.customActionBtn.style

            const isWeb = Platform.OS === 'web'

            if (isWeb) {
              return (
                <img
                  src={config.features.customActionBtn.icon}
                  style={style as React.CSSProperties}
                  alt="customActionBtn"
                />
              )
            }

            return (
              <Image
                accessibilityIgnoresInvertColors
                source={{ uri: config.features.customActionBtn.icon }}
                style={{ objectFit: 'contain', height: 30, width: 150, ...style }}
              />
            )
          }

          return <EllipsisIcon height={30} width={30} fill={businessTheme?.active} />
        } catch (e) {
          console.log('actionOptionIcon.error', e)
          return <EllipsisIcon height={30} width={30} fill={businessTheme?.active} />
        }
      }

      // If the token is a crate, we dont show the header
      if (token.type === 'vatom' && token.isCrate && !unlockKey) return null

      return (
        <Box>
          <HeaderBackOptions
            closeIcon={
              <Box width={34} height={34} justifyContent={'center'} alignItems={'center'}>
                <CloseIcon
                  height={16}
                  width={16}
                  fill={businessTheme?.active ?? theme.colors.darkText}
                />
              </Box>
            }
            showOptions={hideTokenActions}
            onPressOptions={() => setIsFoundationOpen(true)}
            onClose={onClose}
            color={businessTheme?.active}
            optionIcon={actionOptionIcon()}
          />
          {isDeleteOpen && (
            <ActionSheetDelete token={token} isOpen={isDeleteOpen} setOpen={setIsDeleteOpen} />
          )}
          {isDropOpen && (
            <ActionSheetDrop token={token} isOpen={isDropOpen} setOpen={setIsDropOpen} />
          )}
          {isGeneralInfoOpen && (
            <ActionSheetGeneralInfo
              token={token}
              isOpen={isGeneralInfoOpen}
              setOpen={setIsGeneralInfoOpen}
            />
          )}
          {isSendOpen && (
            <ActionSheetSend
              token={token}
              isOpen={isSendOpen}
              setOpen={setIsSendOpen}
              isShare={isShare}
              contractAddress={contractAddress}
              owner={owner}
            />
          )}
          {isSellOpen && (
            <ActionSheetSell token={token} isOpen={isSellOpen} setOpen={setIsSellOpen} />
          )}
          {isUnSellOpen && (
            <ActionSheetUnSell token={token} isOpen={isUnSellOpen} setOpen={setIsUnsellOpen} />
          )}
          {isShareOpen && (
            <ActionSheetShare
              shareLink={shareLink}
              tokenName={tokenName}
              isOpen={isShareOpen}
              setOpen={setIsShareOpen}
            />
          )}
          {isMintOpen && (
            <ActionSheetMint token={token} isOpen={isMintOpen} setOpen={setIsMintOpen} />
          )}
          <ActionSheetFoundation
            token={token}
            unSellAction={() => {
              setIsUnsellOpen(true)
              setIsFoundationOpen(false)
            }}
            sellAction={sellAction}
            sendAction={sendAction}
            mintAction={mintAction}
            splitAction={() => {
              alert('Not yet implemented')
            }}
            dropAction={dropAction}
            pickupAction={pickupAction}
            infoAction={() => {
              setIsGeneralInfoOpen(true)
              setIsFoundationOpen(false)
            }}
            deleteAction={deleteAction}
            shareAction={shareAction}
            shareLinkAction={shareLinkAction}
            isOpen={isFoundationOpen}
            setOpen={setIsFoundationOpen}
          />
        </Box>
      )
    }
  )
)
